
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Observable, ReplaySubject } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { MarineGroup } from '../classes/marine-group';
import { User } from '../classes/user';
import { AlertDialogComponent } from '../components/alert-dialog/alert-dialog.component';
import { AuthService } from './auth.service';
import { GroupsRestService, WorkoutSessionSurveyResponseData } from './groups.rest.service';

const ISO_8601 = 'YYYY-MM-DD';

@Injectable()
export class GroupDataService {
  private _groups: Observable<MarineGroup[]>;
  selectedGroup: MarineGroup;
  selectedGroups: MarineGroup[] = [];
  originalGroup: MarineGroup;
  redrawObs;
  groupDataPoints: any[] = [];
  groupWorkoutSessions: any[] = [];
  groupSurveyResponses: any[] = [];
  selectedDay: moment.Moment;
  get groups(): Observable<MarineGroup[]> {
    if (!this._groups) {
      this._groups = this.restService.getGroups().pipe(
        map((data: any) => {
          return data.map(convertToMarineGroup).sort((a, b) => {
            if (a.name > b.name) {
              return 1;
            } else if (a.name < b.name) {
              return -1;
            } else {
              return 0;
            }
          });
        }),
      );
    }

    return this._groups;
  }

  // testGroups: MarineGroup[] = [];
  get allUsers(): Observable<User[]> {
    return this.restService.getAllUsers().pipe(
      map((data: any) => {
        return data.map(convertToUser);
      }),
    );
  }

  constructor(
    private restService: GroupsRestService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.redrawObs = new ReplaySubject();
  }

  /**
   * Publish the plan to selected groups.
   *
   * @param  {string} planId - id of plan to publish
   * @param  {string[]} groupIDs - IDs of groups to publish to
   */
  publishToGroups(planId: string, groupIDs: string[]) {
    this.restService.publishToGroups(planId, groupIDs).subscribe();
  }

  /**
   * Validates provided list of emails
   * @param emails - emails to validate
   */
  validateEmails(emails: any[]): string[] {
    const invalidEmails = [];

    emails.forEach(email => {
      email = email.trim();
      if (!validateEmail(email)) {
        // keep track of emails that are in invalid format
        invalidEmails.push(email);
      }
    });

    // display modal of invalid email addresses
    if (invalidEmails.length > 0) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          content: 'These emails are invalid:\n' + invalidEmails.join('\n')
        }
      });

      return [];
    }

    return emails;
  }

  /**
   * Open a modal to add owners to the group.
   *
   * @param  {MarineGroup} group - group to add owners to
   * @param  {User[]} users - users to add to group
   */
  addOwnersToGroup(group: MarineGroup, users: User[]) {
    this.restService.addOwners(group.uuid, users.map(user => user.uuid)).subscribe(() => {
      group.addOwners(users);
    });
  }

  /**
   * Remove the marine from a group.
   *
   * @param  {MarineGroup} group - group to remove user from
   * @param  {User} user - user to remove
   * @param  {boolean} request? - flag if the user is in the system or invited
   */
  removeMarine(group: MarineGroup, user: User, request?: boolean) {
    if (request) {
      const i = this.selectedGroup.requestedMembers.indexOf(user);
      this.selectedGroup.requestedMembers.splice(i, 1);
      this.selectedGroup.requestedMembers = this.selectedGroup.requestedMembers.slice();
    } else {
      this.restService.removeMarine(group.uuid, user.uuid)
        .subscribe(() => {
          const i = this.selectedGroup.members.indexOf(user);
          this.selectedGroup.members.splice(i, 1);
          this.selectedGroup.members = this.selectedGroup.members.slice();
        });
    }
  }

  /**
   * Remove owner from the group.
   *
   * @param  {MarineGroup} group - group to remove owner from
   * @param  {User} user - user to remove
   */
  removeOwner(group: MarineGroup, user: User) {
    this.restService.removeOwner(group.uuid, user.uuid).subscribe(() => {
      const i = this.selectedGroup.owners.indexOf(user);
      this.selectedGroup.owners.splice(i, 1);
      this.selectedGroup.owners = this.selectedGroup.owners.slice();
    });
  }

  /**
   * Set a group to the currently viewed group in the service.
   *
   * @param  {string} groupId - id of group
   */
  setSelectedGroup(groupId: string): Observable<MarineGroup> {
    return this.groups.pipe(map((groups: MarineGroup[]) => {
      this.selectedGroup = groups.find(group => group.uuid === groupId);
      return this.selectedGroup;
    }));
  }

  /**
   * Get the attendance statistics for the group.
   *
   * @param  {string} groupId - id of group
   */
  getGroupStats(groupId: string) {
    this.groupDataPoints = [];

    return this.restService.getGroupStats(groupId).pipe(
      map(data => {
        this.groupWorkoutSessions = data;
        const days = this.generateDaysMap();

        data.forEach(workout => {
          const date = moment(workout['date_time']);
          const workout_day = date.format(ISO_8601);

          if (days.has(workout_day)) {
            days.get(workout_day).push({
              workoutType: workout.workoutType,
              attended: workout.attendance,
              date,
            } as any);
          }
        });

        days.forEach((dayWorkouts, dateString: string) => {
          const date = moment(dateString, ISO_8601, true);

          if (dayWorkouts.length === 0) {
            this.groupDataPoints.push({
              workoutType: '',
              attended: 0,
              date,
            });
          } else if (dayWorkouts.length === 1) {
            this.groupDataPoints.push(dayWorkouts[0]);
          } else {
            let attended = 0;
            dayWorkouts.forEach(x => {
              attended = attended + x['attended'];
            });
            const workout: any = {
              workoutType: 'Multiple Stressors',
              attended: attended / dayWorkouts.length,
              date,
            };

            this.groupDataPoints.push(workout);
          }
        });

        this.groupDataPoints.sort(sortDataPoints);
      }));
  }

  /**
   * Get the survey responses for the group.
   *
   * @param  {string} groupId - id of group
   * @returns Observable
   */
  getGroupSurveryResponses(groupId: string): Observable<{
    dataPoints: any[],
    workoutSessionSurveyData: WorkoutSessionSurveyResponseData[]
  }> {
    return this.restService.getGroupSurveryResponses(groupId).pipe(
      map(data => {
        this.groupSurveyResponses = data;
        const days = this.generateDaysMap();

        data.forEach(surveysByGroupWorkoutSession => {
          const dateString = moment(surveysByGroupWorkoutSession.date_time).format(ISO_8601);

          if (days.has(dateString)) {
            days.get(dateString).push(surveysByGroupWorkoutSession);
          }
        });

        const dataPoints: any[] = [];

        days.forEach((workoutSessions, dateString: string) => {
          const date = moment(dateString);

          if (workoutSessions.length === 0) {
            dataPoints.push({
              workoutType: '',
              attended: 0,
              date,
            });
          } else {
            let responded = 0;
            workoutSessions.forEach((x: WorkoutSessionSurveyResponseData) => {
              responded = responded + x.survey_instances.length;
            });
            const dataPoint: any = {
              workoutType: '',
              attended: responded, // / workoutSessions.length,
              date,
            };
            dataPoints.push(dataPoint);
          }
        });

        dataPoints.sort(sortDataPoints);

        return {
          dataPoints,
          workoutSessionSurveyData: this.groupSurveyResponses,
        };
      }));
  }

  /**
   * Get the survey instance from server.
   *
   * @param  {string} surveyInstanceId - id of survey instance
   */
  getSurveyInstanceData(surveyInstanceId: string) {
    return this.restService.getSurveyInstanceData(surveyInstanceId);
  }

  /**
   * Helper function to check if the day exists in the data points.
   *
   * @param  {string} date - date to check
   * @returns boolean
   */
  dayExists(date: string): boolean {
    const momentDate = moment(date);

    return this.groupDataPoints.some(dataPoint => {
      return momentDate.isSame(dataPoint.date, 'day');
    });
  }

  /**
   * Create a new group and save to server.
   *
   * @param  {string} groupName - name of new group
   * @returns void
   */
  createGroup(groupName: string): void {
    const newGroup = new MarineGroup().setName(groupName);

    this.authService.checkForLogin().subscribe((currentUser: User) => {
      // add current user as the owner automatically
      if (currentUser) {
        newGroup.addOwners([currentUser]);
      }

      this.restService.createGroup(newGroup).pipe(
        concatMap((groupData) => {
          this._groups = null;
          return this.setSelectedGroup(groupData['uuid']);
        })).subscribe(() => {
          this.router.navigate(['create-group', this.selectedGroup.uuid]);
        });
    });
  }

  /**
   * Update the group information on the server.
   *
   * @param  {MarineGroup} group - group to upate
   * @returns void
   */
  updateGroup(group: MarineGroup): void {
    this.restService.updateGroup(group).subscribe();
  }

  /**
   * Makes the server call to invite marines by email.
   *
   * @param  {MarineGroup} group - group to add the marines to
   */
  inviteMarines(group: MarineGroup, emails: string[]) {
    this.restService.inviteMarines(group.uuid, emails)
      .subscribe(groupData => {
        group.replaceUsers(groupData['members'].map(convertToUser));
      });
  }

  /**
   * Builds an array of days going back 24 weeks
   * We then summarize data points by these days
   */
  private generateDaysMap(numberOfWeeks: number = 24) {
    const days = new Map<string, any[]>();
    const endDay = moment().endOf('week').add(1, 'days');
    const startDay = moment(endDay).subtract(numberOfWeeks, 'weeks');
    const currentDay = moment(startDay);
    while (!currentDay.isSame(endDay, 'day')) {
      days.set(currentDay.format(ISO_8601), []);
      currentDay.add(1, 'day');
    }

    days.set(currentDay.format(ISO_8601), []);
    return days;
  }
}

/**
 * Sort the data points by date.
 *
 * @param  {any} a
 * @param  {any} b
 */
function sortDataPoints(a: any, b: any) {
  const aDate = a.date;
  const bDate = b.date;

  if (aDate.isBefore(bDate)) { return 1; }
  if (aDate.isAfter(bDate)) { return -1; }
  return 0;
}

/**
 * Convert response for a group into a MarineGroup object.
 *
 * @param  {} groupData - server response
 */
function convertToMarineGroup(groupData) {
  const returnGroup = new MarineGroup();

  returnGroup.uuid = groupData.uuid;
  returnGroup.name = groupData.name;
  returnGroup.addOwners(groupData.owners.map(convertToUser));
  returnGroup.addUsers(groupData.members.map(convertToUser));
  returnGroup.addRequestedUsers(groupData['requested_members'].map(convertToUser));
  returnGroup.privateGroup = groupData.private;

  if (groupData.created_on_timestamp) {
    returnGroup.createdAt = moment(groupData.created_on_timestamp);
  } else {
    returnGroup.createdAt = moment();
  }

  return returnGroup;
}

/**
 * Convert response for a user into a User object.
 *
 * @param  {} userData - server response
 * @returns User
 */
function convertToUser(userData): User {
  const newUser = new User()
    .setUUID(userData.uuid)
    .setFirstName(userData.firstname)
    .setLastName(userData.lastname);

  newUser.email = userData.email;
  newUser.invited = userData.invited;

  return newUser;
}

/**
 * Simple regex to check email format.
 *
 * @param  {string} email
 * @returns boolean
 */
function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
}
